import { useState, useEffect } from 'react'
import NextImage from 'next/image'
import { useTranslation } from 'next-i18next'
import { useSearchParams, useRouter } from 'next/navigation'
import { useIsResponsive } from '@/hooks'
import withNoAuthRoute, { withNoAuthRouteProps } from '@/components/withNoAuthRoute'
import getEMTServerSideProps, { EMTServerSideProps } from '@/helpers/getEMTServerSideProps'
import { SEO } from '@/components'
import { EmtCircularProgress, EmtTypography } from 'emt-components-v2'
import SimpleLayout from '@/components/EMT/SimpleLayout'
import { GetServerSideProps } from 'next'
import { Box } from '@mui/material'
import VariantImages from '@/assets/VariantImages'
import { useNotifications } from '@/modules/Notifications'
import { persistPetSitterFiltersFromSignupStore } from '@/components/pages/sign-up-call-request/persistPetSitterFilters'
import { persistNewRequestCartFromSignupStore } from '@/components/pages/sign-up-call-request/persistNewRequestCart'
import { Routes } from '@/assets/configs'

// Components
import LocationStep from '@/components/pages/sign-up-call-request/LocationStep'
import ServiceStep from '@/components/pages/sign-up-call-request/ServiceStep'
import PetsStep from '@/components/pages/sign-up-call-request/PetsStep'
import AdditionalInformationsStep from '@/components/pages/sign-up-call-request/AdditionalInformationsStep'
import SignUpStep from '@/components/pages/sign-up-call-request/SignUpStep'

import InitialLeftColumn from '@/components/pages/sign-up-call-request/InitialLeftColumn'
import ReservationInfos from '@/components/pages/sign-up-call-request/ReservationInfos'

// Store
import { useEMTDispatch } from '@/store'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'

// API
import { UserServiceHelper } from '@/modules/User'

// Types
import { AnimalData } from '@/components/PetSitter/AddAnimal/AnimalForm'
import { CreateAnimal } from '@/modules/User/apiTypes/ContinueRegister'
import { AnimalSpecies } from '@/dataV3/Animal/AnimalSpecies'
import { PendingAcquisitionUser } from '@/dataV3/User/PendingAcquisitionUser'
import { getClientIp, fetchIpApi, PARIS_CENTER } from '@/utils/search/ipGeo'
import { Address } from '@/dataV3/Common/Address'

const steps = ['location', 'service', 'pets', 'informations', 'signup'] as const
type Step = (typeof steps)[number]

type Props = withNoAuthRouteProps & EMTServerSideProps & { defaultAddress?: Address }

const SignupCallRequestPage: React.FC<Props> = ({ url, title, defaultAddress }) => {
  const { t } = useTranslation('connection')
  const { t: ts } = useTranslation('sign-up-call-request')
  const searchParams = useSearchParams() // app router
  const [addNotificationError] = useNotifications()
  const router = useRouter()
  const dispatch = useEMTDispatch()
  const stepFromUrl = (searchParams.get('step') as Step) || 'location'
  const [isMobile] = useIsResponsive()
  const [isLoading, setIsLoading] = useState(false)

  const [step, setStep] = useState<Step>(stepFromUrl)

  // Zustand store
  const animals = useSignUpCallRequestStore((s) => s.animals)

  // Listen to query param changes and update step
  useEffect(() => {
    if (step !== stepFromUrl) {
      setStep(stepFromUrl)
    }
  }, [stepFromUrl])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' })
    }
  }, [step])

  const goTo = (next: Step) => {
    setStep(next)
    router.push(`?step=${next}`, { scroll: false })
  }

  const stepTitleKeys: Record<Step, string> = {
    location: 'stepTitle.location',
    service: 'stepTitle.service',
    pets: 'stepTitle.pets',
    informations: 'stepTitle.informations',
    signup: 'stepTitle.signup',
  }

  const rightContent = (
    <EmtCircularProgress
      stepTitle={t('stepTitle') as string}
      color="white"
      value={((steps.indexOf(step) + 1) / steps.length) * 100}
      currentStep={steps.indexOf(step) + 1}
      totalStep={steps.length}
      currentStepTitle={ts(stepTitleKeys[step]) as string}
    />
  )

  const handlePetsSubmit = async (animals: AnimalData[]) => {
    try {
      for (const animal of animals) {
        const isoBirthDate = new Date(animal.birthDate).toISOString()
        const requestBody: CreateAnimal.Body = {
          speciesSlug: animal.selectedRadio || AnimalSpecies.dog,
          displayName: animal.displayName,
          breedSlug: animal.breedSlug,
          sizeSlug: animal.sizeSlug,
          birthDate: isoBirthDate,
          gender: animal.gender || '',
          mainPicture: animal.mainPicture,
          animalPictures: animal.animalPictures,
          description: animal.description,
          animalOptions: {
            niceWithChildren: animal.affinities?.niceWithChildren || false,
            niceWithDogs: animal.affinities?.niceWithDogs || false,
            niceWithCats: animal.affinities?.niceWithCats || false,
            entrustFewHours: animal.times?.entrustFewHours || false,
            entrustDayNight: animal.times?.entrustDayNight || false,
            entrustWeekendHoliday: animal.times?.entrustWeekendHoliday || false,
            availabilityWeekDay: animal.availability?.availabilityWeekDay || false,
            availabilityWeekNight: animal.availability?.availabilityWeekNight || false,
            availabilityWeekend: animal.availability?.availabilityWeekend || false,
            needTrips: animal.needs?.needTrips || false,
            needExercise: animal.needs?.needExercise || false,
            needPlay: animal.needs?.needPlay || false,
            needKeep: animal.needs?.needKeep || false,
            needTakeCare: animal.needs?.needTakeCare || false,
            needLove: animal.needs?.needLove || false,
            sterilized: animal.sterilized || false,
            displayToBorrowers: animal.showVolunteer || false,
          },
        }

        await UserServiceHelper.dispatchCreatePendingAnimalAsync(dispatch)(requestBody)
      }

      // Fetch updated user data after all animals are processed
      const updatedPendingUser = await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()
      return updatedPendingUser
    } catch (error) {
      addNotificationError({
        error,
      })
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const completeRegistration = async () => {
    setIsLoading(true)
    try {
      // Create Pets and get updated pending user
      const updatedPendingUser = await handlePetsSubmit(animals)
      // Finalize Inscription
      await UserServiceHelper.dispatchCompleteRegisterAsync(dispatch)({ santevet: false })
      // Persist Pet Sitter Filters
      if (typeof window !== 'undefined') {
        localStorage.setItem('SearchPetSitterTipsModal', 'true')
        persistPetSitterFiltersFromSignupStore()
        persistNewRequestCartFromSignupStore(updatedPendingUser as PendingAcquisitionUser)
      }
      // Redirect to petsitter search
      router.push(Routes.PetSitter.Search)
    } catch (error) {
      addNotificationError({
        error,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <SEO url={`${url}`} subtitle={title} />
      <SimpleLayout rightContent={rightContent} pro maxWidth="lg">
        <Box
          sx={{
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column-reverse' : 'initial',
            gridTemplateColumns: '5fr 9fr',
            gap: isMobile ? '16px' : '50px',
            position: 'relative',
            minWidth: 0,
          }}
        >
          {step === 'location' && <InitialLeftColumn />}
          {(step === 'service' || step === 'pets' || step === 'informations' || step === 'signup') && (
            <ReservationInfos step={step} />
          )}

          {/* Content */}
          <Box
            sx={{
              marginBottom: isMobile ? '20px' : 0,
              marginTop: isMobile ? '0px' : '40px',
              minWidth: 0,
            }}
          >
            {step === 'location' && (
              <>
                {isMobile && (
                  <Box sx={{ marginBottom: '20px' }}>
                    <EmtTypography
                      variant={'h4'}
                      fontWeight="800"
                      sx={{ fontSize: isMobile ? '18px' : '24px', display: 'inline-block', marginRight: '0.3em' }}
                    >
                      Trouvez facilement un{' '}
                    </EmtTypography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: '0.3em' }}>
                      <EmtTypography
                        variant={'h4'}
                        fontWeight="800"
                        color="primary"
                        sx={{ fontSize: isMobile ? '18px' : '24px' }}
                      >
                        {' '}
                        Pet
                      </EmtTypography>
                      <NextImage
                        className="inline"
                        width={isMobile ? 50 : 70}
                        height={isMobile ? 25 : 33}
                        src={VariantImages.Sitter}
                        alt="sitter"
                      />
                    </Box>
                    <EmtTypography variant={'h4'} fontWeight="800" sx={{ fontSize: isMobile ? '18px' : '24px' }}>
                      pour votre animal
                    </EmtTypography>
                  </Box>
                )}
                <LocationStep defaultAddress={defaultAddress} onNext={() => goTo('service')} />
              </>
            )}
            {step === 'service' && <ServiceStep onNext={() => goTo('pets')} />}
            {step === 'pets' && <PetsStep onNext={() => goTo('informations')} />}
            {step === 'informations' && <AdditionalInformationsStep onNext={() => goTo('signup')} />}
            {step === 'signup' && (
              <SignUpStep tendersEnabled={false} loading={isLoading} onNext={() => completeRegistration()} />
            )}
          </Box>
        </Box>
      </SimpleLayout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const base = await getEMTServerSideProps({
    namespaces: ['common', 'connection', 'petsitter', 'sign-up-call-request', 'premium'],
    i18nTitleNs: 'sign-up-call-request',
    i18nTitleKey: 'title',
  })(ctx)

  if ('redirect' in base || 'notFound' in base) return base

  const ip = getClientIp(ctx.req)
  const defaultAddress = (await fetchIpApi(ip)) ?? PARIS_CENTER

  return {
    ...base,
    props: {
      ...base.props,
      defaultAddress,
    },
  }
}

export default withNoAuthRoute(Routes.PetSitter.Search)(SignupCallRequestPage)
